import { useMemo } from 'react'
import {
    NyuDataPointWithParsedTime,
    NyuDataPointsWithParsedTime,
    NyuDataProperties,
    NyuReferenceLine,
    NyuSeriesProperties,
} from '../../../shared/types'
import { getGroupsFromSeriesProperties, toBaseDataKey } from './helpers'

const isDerivedDataKey = (key: string) => toBaseDataKey(key) !== key

export type ResolvedReferenceLine = NyuReferenceLine & {
    id: string
    value: number
}

export const defaultReferenceLineColor = '#000000'

const toFiniteNumber = (value: unknown): number | undefined => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value
    }
    return undefined
}

// a group is one legend-entry and therefore holds at most one value per
// data-point: its forecast key continues the base key where that one ends
function valueOfGroupAt(
    dataPoint: NyuDataPointWithParsedTime,
    keys: string[]
): number | undefined {
    for (const key of keys) {
        const value = toFiniteNumber(dataPoint[key])
        if (value !== undefined) {
            return value
        }
    }
    return undefined
}

function averageOfGroup(
    groupId: string,
    seriesProperties: NyuSeriesProperties,
    dataPoints: NyuDataPointsWithParsedTime
): number | undefined {
    const group = getGroupsFromSeriesProperties(seriesProperties).find(
        ({ id }) => id === groupId
    )

    if (!group) {
        return undefined
    }

    // the same key can appear once per stack, and the base key wins over its
    // forecast or highlight where a data-point carries both
    const keys = [...new Set(group.lines.map(({ key }) => key))].sort(
        (a, b) => Number(isDerivedDataKey(a)) - Number(isDerivedDataKey(b))
    )

    const values = dataPoints.flatMap((dataPoint) => {
        const value = valueOfGroupAt(dataPoint, keys)
        return value === undefined ? [] : [value]
    })

    if (values.length === 0) {
        return undefined
    }

    return values.reduce((sum, value) => sum + value, 0) / values.length
}

// Labels of averaged reference lines may contain `{years}`, which is replaced
// with the year-range that is actually displayed (the year-filter changes it).
const yearsPlaceholder = '{years}'

function resolveLabel(
    label: string | undefined,
    dataPoints: NyuDataPointsWithParsedTime
): string | undefined {
    if (!label || !label.includes(yearsPlaceholder)) {
        return label
    }

    const years = dataPoints
        .map(({ year }) => year)
        .filter((year) => Number.isFinite(year) && year > 0)

    const yearRange =
        years.length === 0
            ? ''
            : `${Math.min(...years)}\u2013${Math.max(...years)}`

    return label.replace(yearsPlaceholder, yearRange).trim()
}

/*
Reference lines are horizontal (or vertical, in vertical bar-charts) lines drawn
on top of the chart. Their `value` is either a number, which positions the line
directly, or the id of a series-group, whose mean over the displayed data-points
is used - so that averaged lines follow the year-filter.
*/
function resolveReferenceLines(
    dataProperties: NyuDataProperties,
    seriesProperties: NyuSeriesProperties,
    dataPoints: NyuDataPointsWithParsedTime
): ResolvedReferenceLine[] {
    const referenceLines = dataProperties.referenceLines || []

    return referenceLines.flatMap((referenceLine, index) => {
        const { value } = referenceLine

        const resolvedValue =
            typeof value === 'number'
                ? value
                : averageOfGroup(value, seriesProperties, dataPoints)

        if (resolvedValue === undefined) {
            console.error(
                `Error: reference line #${index} has no value (group=${value})`
            )
            return []
        }

        return [
            {
                ...referenceLine,
                label: resolveLabel(referenceLine.label, dataPoints),
                id: `reference-line-${index}`,
                value: resolvedValue,
            },
        ]
    })
}

export function useReferenceLines(
    dataProperties: NyuDataProperties,
    seriesProperties: NyuSeriesProperties,
    dataPoints: NyuDataPointsWithParsedTime
): ResolvedReferenceLine[] {
    return useMemo(
        () =>
            resolveReferenceLines(dataProperties, seriesProperties, dataPoints),
        [dataProperties, seriesProperties, dataPoints]
    )
}
